import type { ReactNode } from 'react'

export type ChromeDropZoneOverlayProps = {
	/** 是否显示 */
	active: boolean
	/** 主提示文字 */
	message?: string
	/** 副提示（可选） */
	hint?: string
	/** 自定义图标（默认是一个云/上传 glyph） */
	icon?: ReactNode
}

/**
 * 拖拽放置区域的全屏覆盖层。
 * <p>
 * 视觉风格对齐 ChromeMediaUploadSlot 空态：
 * - 虚线边框用 accent
 * - 面板背景用 panel + 轻微 surface-muted 叠底
 * - 图标容器用 menu-solid + line 边框（与上传槽一致）
 * - 所有颜色走主题 CSS 变量，不硬编码色调
 */
export function ChromeDropZoneOverlay({
	active,
	message = '松开鼠标以上传参考图',
	hint,
	icon,
}: ChromeDropZoneOverlayProps) {
	if (!active) return null

	return (
		<div
			className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center"
			aria-hidden
		>
			{/* 背景遮罩：用 panel 叠 surface-muted，与项目主题一致 */}
			<div
				className="absolute inset-0"
				style={{
					background:
						'color-mix(in srgb, var(--panel) 70%, transparent)',
				}}
			/>
			{/* 外框：accent 虚线 */}
			<div
				className="absolute inset-2 rounded-[var(--radius-lg)] border-2 border-dashed"
				style={{
					borderColor: 'color-mix(in srgb, var(--accent) 55%, var(--line) 35%)',
					background:
						'color-mix(in srgb, var(--surface-muted) 45%, transparent)',
				}}
			/>
			{/* 居中容器 */}
			<div
				className="relative flex flex-col items-center gap-3 rounded-[var(--radius-lg)] border px-8 py-6 shadow-card"
				style={{
					borderColor: 'var(--line)',
					background: 'var(--menu-solid)',
				}}
			>
				<span
					className="flex h-12 w-12 items-center justify-center rounded-full border"
					style={{
						borderColor: 'var(--line)',
						background: 'color-mix(in srgb, var(--panel) 55%, var(--surface-muted))',
					}}
				>
					{icon ?? (
						<ChromeDropIcon className="h-6 w-6" />
					)}
				</span>
				<span
					className="text-sm font-medium"
					style={{ color: 'var(--accent)' }}
				>
					{message}
				</span>
				{hint && (
					<span className="text-xs text-ink-soft">{hint}</span>
				)}
			</div>
		</div>
	)
}

/**
 * 云+箭头的极简上传图标（内联 SVG，避免额外依赖）。
 */
function ChromeDropIcon({ className = '' }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.7"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="M12 16V7" />
			<path d="m8 10 4-4 4 4" />
			<path d="M5 16a4 4 0 0 1 .7-7.94 6 6 0 0 1 11.2 1.14A3.5 3.5 0 0 1 18 16Z" />
		</svg>
	)
}
